import React, { useState, Component } from 'react';
import { ClockIcon, ShieldAlertIcon, LockIcon } from 'lucide-react';
import { BusinessHistory, BusinessEvent, sampleInvoiceBusinessEvents } from './BusinessHistory';
import { RegulatoryAuditTrail, AuditEntry, sampleInvoiceAuditEntries } from './RegulatoryAuditTrail';
// Re-export types for convenience
export type { BusinessEvent, BusinessEventCategory, ActorType } from './BusinessHistory';
export type { AuditEntry, AuditCategory, RuleResult, IntegrationDirection, FieldChangeEntry, ActionEntry, RuleEvaluationEntry, WorkflowTransitionEntry, IntegrationEntry } from './RegulatoryAuditTrail';
export interface DocumentTraceabilityProps {
  entityType: string;
  entityId: string;
  businessEvents: BusinessEvent[];
  auditEntries: AuditEntry[];
  hasAuditAccess: boolean;
  locale?: string;
}
type TabId = 'history' | 'audit';
// Sample comprehensive mock data for demonstration
export const sampleDocumentTraceabilityData = {
  entityType: 'Invoice',
  entityId: 'INV-2024-0892',
  businessEvents: sampleInvoiceBusinessEvents,
  auditEntries: sampleInvoiceAuditEntries
};
export function DocumentTraceability({
  entityType,
  entityId,
  businessEvents,
  auditEntries,
  hasAuditAccess,
  locale = 'en-US'
}: DocumentTraceabilityProps) {
  const [activeTab, setActiveTab] = useState<TabId>('history');
  const tabs: {
    id: TabId;
    label: string;
    icon: React.ElementType;
  }[] = [{
    id: 'history',
    label: 'History',
    icon: ClockIcon
  }, {
    id: 'audit',
    label: 'Audit Trail',
    icon: ShieldAlertIcon
  }];
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-2093">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-2094">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-2095">
          Document Traceability
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2096">
          View history and audit trail for {entityType} {entityId}
        </p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200" data-id="element-2097">
        <nav className="-mb-px flex" aria-label="Tabs" data-id="element-2098">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          const IconComponent = tab.icon;
          const showLock = tab.id === 'audit' && !hasAuditAccess;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                  relative flex-1 py-4 px-6 text-center text-sm font-medium transition-colors
                  ${isActive ? 'border-b-2 border-indigo-500 text-indigo-600' : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `} aria-current={isActive ? 'page' : undefined} data-id="element-2099">
                <span className="flex items-center justify-center" data-id="element-2100">
                  <IconComponent className={`h-5 w-5 mr-2 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`} data-id="element-2101" />
                  {tab.label}
                  {showLock && <LockIcon className="h-4 w-4 ml-1.5 text-gray-400" data-id="element-2102" />}
                </span>
              </button>;
        })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="p-6" data-id="element-2103">
        {activeTab === 'history' && <BusinessHistory entityType={entityType} entityId={entityId} events={businessEvents} locale={locale} data-id="element-2104" />}

        {activeTab === 'audit' && <RegulatoryAuditTrail entityType={entityType} entityId={entityId} auditEntries={auditEntries} hasAuditAccess={hasAuditAccess} locale={locale} data-id="element-2105" />}
      </div>
    </div>;
}
// Standalone demo component with mock data
export function DocumentTraceabilityDemo() {
  const [hasAccess, setHasAccess] = useState(true);
  return <div className="space-y-4" data-id="element-2106">
      {/* Toggle for demo purposes */}
      <div className="flex items-center justify-end space-x-2 px-4" data-id="element-2107">
        <label className="text-sm text-gray-600" data-id="element-2108">Audit Access:</label>
        <button onClick={() => setHasAccess(!hasAccess)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hasAccess ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2109">
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hasAccess ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2110" />
        </button>
        <span className="text-sm text-gray-500" data-id="element-2111">
          {hasAccess ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <DocumentTraceability entityType={sampleDocumentTraceabilityData.entityType} entityId={sampleDocumentTraceabilityData.entityId} businessEvents={sampleDocumentTraceabilityData.businessEvents} auditEntries={sampleDocumentTraceabilityData.auditEntries} hasAuditAccess={hasAccess} data-id="element-2112" />
    </div>;
}